import { argon2id } from 'hash-wasm';

export interface DerivedKeys {
  authKey: string;
  masterKey: Uint8Array;
}

/**
 * Derive authentication and encryption keys from password using Argon2id.
 * Uses email as a component of the salt to ensure different users get different keys.
 * 
 * @param password - User's password
 * @param email - User's email (used in salt)
 * @returns Object containing authKey (hex string for Supabase) and masterKey (bytes for encryption)
 */
export async function deriveKeys(password: string, email: string): Promise<DerivedKeys> {
  const normalizedEmail = email.toLowerCase().trim();
  
  // Derive authKey for Supabase authentication
  const authKey = await argon2id({
    password,
    salt: `consl-auth-${normalizedEmail}`,
    parallelism: 1,
    iterations: 3,
    memorySize: 65536, // 64MB
    hashLength: 32,
    outputType: 'hex',
  });
  
  // Derive masterKey for encryption (binary output)
  const masterKey = await argon2id({
    password,
    salt: `consl-encryption-${normalizedEmail}`,
    parallelism: 1,
    iterations: 3,
    memorySize: 65536, // 64MB
    hashLength: 32,
    outputType: 'binary',
  });
  
  return { authKey, masterKey };
}

/**
 * Derive only the master key (used when unlocking from existing session).
 */
export async function deriveMasterKey(password: string, email: string): Promise<Uint8Array> {
  const normalizedEmail = email.toLowerCase().trim();
  
  return argon2id({
    password,
    salt: `consl-encryption-${normalizedEmail}`,
    parallelism: 1,
    iterations: 3,
    memorySize: 65536,
    hashLength: 32,
    outputType: 'binary',
  });
}
